import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import userStore from '../../../stores/user.store';
import { useObserver } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import serverStore from '../../../stores/server.store';
import HeaderSearch from './HeaderSearch';
import { Icon } from '@blueprintjs/core';
import { useHistory } from 'react-router-dom';
import ChangesModal from '../modal/ChangesModal';
import {
    EuiButton, EuiFlyout,
    EuiFlyoutBody,
    EuiPopover,
    EuiContextMenu,
    useGeneratedHtmlId
} from '@elastic/eui';
import uptimeStore from '../../../stores/uptime.store';
import mongoDbStore from '../../../stores/mongodb.store';
import { startPolling } from '../../../servises/polling.service';
import { addToast } from '../toasts/Toast';

const HeaderStyle = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  
  border-bottom: 1px solid var(--border-color);
  background-color: rgba(19, 19, 22, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: rgba(0, 0, 0, 0.2) 0px 4px 12px;

  .donate-link {
    color: var(--color-primary);
  }
`;

const HeaderSectionStyle = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  @media (min-width: 600px) {
    height: 52px;
  }
  padding: 0 16px;
`;

const HeaderSectionLeftStyle = styled.div`
  display: grid;
  grid-template-columns: max-content auto auto 1fr;
  grid-gap: 20px;
  align-items: center;

  .buttonLink {
    width: fit-content;
  }

  .desktop-links {
    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const MenuIconStyled = styled.div`
  display: block;

  path {
    fill: var(--color-primary);
  }
`;

const HeaderSectionRightStyle = styled.div`
  display: grid;
  grid-template-columns: auto max-content;
  grid-gap: 20px;
  justify-items: end;

  .desktop-left-menu {
    display: grid;
    grid-template-columns: auto auto auto auto max-content;
    grid-gap: 12px;
    justify-items: end;
    align-items: center;

    @media (max-width: 600px) {
      display: none;
    }
  }

  .header-link {
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;
    padding: 6px 10px;
    border-radius: 6px;

    &:hover {
      color: var(--text-primary);
      background: rgba(255, 255, 255, 0.04);
      text-decoration: none;
    }
  }

  .mobile-left-menu {
    display: none;
    font-size: 55px;

    @media (max-width: 600px) {
      display: block;
    }
  }

  .buy-me-coffee {
    display: inline-flex;
    align-items: center;
    img {
      height: 28px;
    }
  }
`;

const LogoStyle = styled.div`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;

  a {
    display: flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    color: #fff !important;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 0.9;
      text-decoration: none;
    }
  }

  span {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: var(--color-primary);
    background: rgba(var(--color-primary-rgb), 0.12);
    border: 1px solid rgba(var(--color-primary-rgb), 0.25);
    padding: 1px 5px;
    border-radius: 4px;
    margin-left: 6px;
  }
`;

const UserChipStyled = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 6px 12px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bg-surface-hover);
    border-color: var(--color-primary);
  }
  
  &::after {
    content: "";
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid var(--text-secondary);
  }
`;

const Header = (props) => {
    const history = useHistory();

    const [drawlerOpened, setDrawlerOpened] = useState(false);
    const [changesModalShow, setChangesModalShow] = useState(false);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const onButtonClick = () =>
        setIsPopoverOpen((isPopoverOpen) => !isPopoverOpen);
    const closePopover = () => setIsPopoverOpen(false);

    const contextMenuPopoverId = useGeneratedHtmlId({
        prefix: 'contextMenuPopover'
    });

    useEffect(() => {
        return startPolling(() => {
            return Promise.all([
                serverStore.getServers(true),
                uptimeStore.getUptime(true),
                mongoDbStore.getMongoDb(true)
            ]);
        }, 60000, error => {
            addToast(error.message || 'Failed to refresh monitors', 'error', 'monitor-refresh');
        });
    }, []);

    const logout = () => {
        return userStore.doLogOut();
    };

    const navigateToNewServer = e => {
        e.preventDefault();
        history.push('/servers-new');
    };

    const emailButton = (
        <UserChipStyled onClick={ onButtonClick }>
            { userStore.user ? userStore.user.email : 'No Email' }
        </UserChipStyled>
    );

    const panels = [
        {
            id: 0,
            title: 'Settings',
            items: [
                {
                    name: 'Account Settings',
                    onClick: () => {
                        history.push('/settings');
                    }
                },
                {
                    name: 'Support',
                    href: 'https://github.com/syagent/agent-2/issues',
                    target: '_blank'
                },
                {
                    name: 'Suggest',
                    href: 'https://github.com/syagent/agent-2/discussions',
                    target: '_blank'
                },
                {
                    name: 'Logout',
                    onClick: logout
                }
            ]
        }
    ];

    return useObserver(() => (
        <HeaderStyle className="header">
            <HeaderSectionStyle>
                <HeaderSectionLeftStyle>
                    <LogoStyle>
                        <Link to={ `/servers` }>
                            SyAgent
                            <span>BETA</span>
                        </Link>
                    </LogoStyle>
                    <HeaderSearch/>

                    <EuiButton fill size={ 's' }
                               iconType={ 'plus' }
                               className="desktop-links"
                               onClick={ navigateToNewServer }>Add new
                        monitor</EuiButton>

                </HeaderSectionLeftStyle>
                <HeaderSectionRightStyle>
                    <div className="mobile-left-menu">
                        <MenuIconStyled
                            onClick={ () => setDrawlerOpened(!drawlerOpened) }
                        >
                            <Icon icon="menu" size={ 24 } indent="none"/>
                        </MenuIconStyled>
                        { drawlerOpened && (
                            <>
                                <EuiFlyout
                                    size="l"
                                    ownFocus={ true }
                                    outsideClickCloses={ true }
                                    onClose={ () => setDrawlerOpened(false) }
                                >
                                    <EuiFlyoutBody>
                                        <div className="mobile-menu-wrapper">
                                            <EuiButton fill size={ 's' }
                                                       iconType={ 'plus' }
                                                       className="desktop-links"
                                                       isDisabled={ !serverStore.canAddNewServers }
                                                       onClick={ navigateToNewServer }>Add new
                                                 monitor</EuiButton>

                                            <a href="https://www.buymeacoffee.com/syagent" target="_blank" rel="noreferrer" className={ 'header-link buy-me-coffee' }><img src="/assets/bmc-button.png" alt="buy me a coffee"/></a>
                                            <Link to={ `/` }
                                                  className={ 'header-link' }>Monitors
                                                List</Link>

                                            <Link to={ `/alarms-settings` }
                                                  className={ 'header-link' }>Alarms
                                                Settings</Link>

                                            <Link to={ `/settings` }
                                                  className={ 'header-link' }>Settings</Link>

                                            <a target="_blank" rel="noreferrer"
                                               href="https://github.com/syagent/agent-2/issues">Support</a>
                                            <a target="_blank" rel="noreferrer"
                                               href="https://github.com/syagent/agent-2/discussions">Suggest</a>

                                            <div className="logOut"
                                                 onClick={ logout }>Logout
                                            </div>
                                        </div>
                                    </EuiFlyoutBody>
                                </EuiFlyout>
                            </>
                        ) }

                    </div>
                    <div className="desktop-left-menu">
                        <a href="https://www.buymeacoffee.com/syagent" target="_blank" rel="noreferrer" className={ 'header-link buy-me-coffee' }><img src="/assets/bmc-button.png" alt="buy me a coffee"/></a>
                        <Link to={ `/` }
                              className={ 'header-link' }>Monitors</Link>
                        <Link to={ `/alarms-settings` }
                              className={ 'header-link' }>Alarms Settings</Link>

                        <EuiPopover
                            id={ contextMenuPopoverId }
                            button={ emailButton }
                            isOpen={ isPopoverOpen }
                            panelPaddingSize="none"
                            closePopover={ closePopover }
                        >
                            <EuiContextMenu initialPanelId={ 0 } panels={ panels }/>
                        </EuiPopover>
                    </div>
                </HeaderSectionRightStyle>
            </HeaderSectionStyle>
            { changesModalShow && (
                <ChangesModal close={ () => setChangesModalShow(false) }/>
            ) }
        </HeaderStyle>
    ));
};

export default Header;
